import React, { useState, useEffect, useCallback } from 'react';
import './BulkDeleteModal.css';

const BulkDeleteModal = ({ 
  isOpen, 
  onClose, 
  title,
  items,
  onDelete,
  getItemId,
  getItemLabel,
  entityName = 'elemento',
  onComplete
}) => {
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [deletedItems, setDeletedItems] = useState([]);
  const [failedItems, setFailedItems] = useState([]);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setDeleting(false);
      setProgress(0);
      setCurrentIndex(0);
      setDeletedItems([]);
      setFailedItems([]);
      setResults(null);
    }
  }, [isOpen]);

  const handleDelete = useCallback(async () => {
    if (!items || items.length === 0) return;

    setDeleting(true);
    setProgress(0);
    setCurrentIndex(0);
    setDeletedItems([]);
    setFailedItems([]);

    const total = items.length;
    const deleted = [];
    const failed = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const id = getItemId(item);
      const label = getItemLabel ? getItemLabel(item) : id;

      try {
        await onDelete(id, item);
        deleted.push({ id, label, success: true });
        setDeletedItems([...deleted]);
      } catch (error) {
        failed.push({ id, label, error: error.message, success: false });
        setFailedItems([...failed]);
      }

      setCurrentIndex(i + 1);
      setProgress(((i + 1) / total) * 100);
    }

    setResults({
      total,
      success: deleted.length,
      failed: failed.length
    });
    setDeleting(false);
  }, [items, onDelete, getItemId, getItemLabel, entityName]);

  const handleClose = () => {
    if (!deleting) {
      onClose();
      if (onComplete) onComplete();
    }
  };

  if (!isOpen) return null;

  const progressPercentage = Math.round(progress);

  return (
    <div className="bulk-delete-overlay">
      <div className="bulk-delete-modal">
        <div className="bulk-delete-header">
          <h3>{title || `Eliminar ${entityName}s`}</h3>
          {!deleting && !results && (
            <button className="bulk-delete-close" onClick={handleClose}>×</button>
          )}
        </div>

        <div className="bulk-delete-content">
          {!deleting && !results && (
            <>
              <div className="bulk-delete-warning">
                <p>¿Estás seguro que deseas eliminar los siguientes {entityName}s?</p>
                <p className="bulk-delete-count">{items.length} {entityName}{items.length !== 1 ? 's' : ''} seleccionado{items.length !== 1 ? 's' : ''}</p>
              </div>
              
              <div className="bulk-delete-list">
                {items.map((item, index) => (
                  <div key={getItemId(item) || index} className="bulk-delete-item">
                    <span className="bulk-delete-item-label">
                      {getItemLabel ? getItemLabel(item) : getItemId(item)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {deleting && (
            <div className="bulk-delete-progress-container">
              <div className="bulk-delete-progress-info">
                <p>Eliminando {currentIndex} de {items.length}...</p>
                <p className="bulk-delete-progress-percent">{progressPercentage}%</p>
              </div>
              
              <div className="bulk-delete-progress-bar">
                <div 
                  className="bulk-delete-progress-fill" 
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>

              <div className="bulk-delete-items-status">
                <div className="bulk-delete-deleted">
                  <span>Eliminados: {deletedItems.length}</span>
                </div>
                {failedItems.length > 0 && (
                  <div className="bulk-delete-failed">
                    <span>Fallidos: {failedItems.length}</span>
                  </div>
                )}
              </div>

              <div className="bulk-delete-current">
                <p className="bulk-delete-current-label">Eliminando:</p>
                <ul className="bulk-delete-current-list">
                  {deletedItems.slice(-3).map((item, idx) => (
                    <li key={idx} className="deleted">✓ {item.label}</li>
                  ))}
                  {failedItems.slice(-3).map((item, idx) => (
                    <li key={`failed-${idx}`} className="failed">✗ {item.label}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {results && (
            <div className="bulk-delete-results">
              <div className={`bulk-delete-result-icon ${results.failed === 0 ? 'success' : 'partial'}`}>
                {results.failed === 0 ? '✓' : '⚠'}
              </div>
              
              <div className="bulk-delete-result-summary">
                <h4>
                  {results.failed === 0 
                    ? 'Eliminación completada' 
                    : 'Eliminación parcialmente completada'}
                </h4>
                <p>{results.success} {entityName}{results.success !== 1 ? 's' : ''} eliminado{results.success !== 1 ? 's' : ''}</p>
                {results.failed > 0 && (
                  <p className="bulk-delete-result-failed">{results.failed} {entityName}{results.failed !== 1 ? 's' : ''} no se pudieron eliminar</p>
                )}
              </div>

              {failedItems.length > 0 && (
                <div className="bulk-delete-failed-list">
                  <h5>Errores:</h5>
                  {failedItems.map((item, idx) => (
                    <div key={idx} className="bulk-delete-failed-item">
                      <span className="failed-label">{item.label}</span>
                      <span className="failed-error">{item.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bulk-delete-footer">
          {!deleting && !results && (
            <>
              <button 
                className="bulk-delete-cancel" 
                onClick={handleClose}
              >
                Cancelar
              </button>
              <button 
                className="bulk-delete-confirm" 
                onClick={handleDelete}
              >
                Eliminar {items.length} {entityName}{items.length !== 1 ? 's' : ''}
              </button>
            </>
          )}
          
          {results && (
            <button 
              className="bulk-delete-confirm" 
              onClick={handleClose}
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteModal;
